import React from 'react';
import { Building2, Plus, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';

export const ExtCompaniesTable = ({
  companies,
  totalItems,
  totalPages,
  page,
  setPage,
  pageSize,
  setPageSize,
  pageSizeOptions,
  rangeStart,
  rangeEnd,
  searchQuery,
  setSearchQuery,
  filterActive,
  setFilterActive,
  onCompanyClick,
  onCreate,
  selectedCompanyId,
  readOnlyMode
}) => {
  const hasActiveFilters = searchQuery !== '' || filterActive !== 'all';

  const clearFilters = () => {
    setSearchQuery('');
    setFilterActive('all');
  };

  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0 min-h-[420px] flex flex-col">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-700 shrink-0">
        {/* Header with title and badge */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <Building2 className="w-5 h-5 text-gray-400 shrink-0" />
            <h2 className="text-xl font-semibold text-white">
              External Companies
            </h2>
            <Badge className="bg-blue-900/50 text-blue-200">
              {totalItems} {totalItems === 1 ? 'company' : 'companies'}
            </Badge>
          </div>
          {/* Create button only visible to higher-ups*/}
          {!readOnlyMode && (
            <button
              type="button"
              onClick={onCreate}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 hover:border-green-700 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              Add Company
            </button>
          )}
        </div>

        {/* Filters section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 w-full">
            {/* Search bar */}
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder="Search companies by name, tax ID, email, contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-0 outline-0 text-white placeholder-gray-400 text-sm w-full"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Filter by status */}
            <div className="flex-1 min-w-[150px]">
              <select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
            {/*Clear filters button */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-red-600 bg-red-600/20 text-red-300 text-sm font-medium hover:bg-red-600/30 transition-colors cursor-pointer shrink-0"
                title="Clear all filters"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Companies List */}
      <div className="p-4 sm:p-6 w-full min-w-0 flex-1 flex flex-col">
        {companies.length === 0 && totalItems === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              No companies registered.
            </p>
          </div>
        ) : companies.length === 0 && totalItems > 0 ? (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              No companies found matching your search.
            </p>
          </div>
        ) : (
          <>
            <div className="w-full flex-1 min-h-0 overflow-y-auto">
              {/* Card View for mobile */}
              <div className="grid grid-cols-1 gap-4 sm:hidden">
                {companies.map((company) => (
                  <div
                    key={company.id}
                    onClick={() => onCompanyClick(company)}
                    className={`bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-sm hover:bg-gray-750 transition-colors cursor-pointer ${selectedCompanyId === company.id ? 'ring-2 ring-blue-500 ring-inset' : ''
                      }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-semibold text-white">
                        {company.name || 'N/A'}
                      </h3>
                      <Badge className={company.active ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}>
                        {company.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-400">Tax ID:</span>
                        <span className="text-gray-300 font-mono">{company.tax_id || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-400">Contact:</span>
                        <span className="text-gray-300">{company.contact_name || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-400">Email:</span>
                        <span className="text-gray-300 truncate max-w-[180px]">{company.email || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Table View for desktop */}
              <div className="hidden sm:block w-full">
                <div className="inline-block w-full align-middle">
                  <div className="shadow ring-1 ring-gray-700 rounded-lg w-full">
                    <table className="w-full divide-y divide-gray-700">
                      <thead className="sticky top-0 z-0 bg-gray-700/50 shadow-sm">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Tax ID
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Phone
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {companies.map((company, index) => (
                          <tr
                            key={company.id}
                            onClick={() => onCompanyClick(company)}
                            className={`transition-colors cursor-pointer ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'
                              } hover:bg-gray-700 ${selectedCompanyId === company.id ? 'ring-2 ring-blue-500 ring-inset' : ''
                              }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-left">
                              <span className="text-sm font-medium text-white">
                                {company.name || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-mono text-gray-300">
                              {company.tax_id || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-center">
                              {company.contact_name || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-center">
                              {company.email || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-center">
                              {company.phone || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <Badge className={company.active ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}>
                                {company.active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
              <div className="px-4 sm:px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
                <div className="flex items-center gap-4 flex-wrap">
                  <p className="text-sm text-gray-500">
                    Showing {rangeStart}–{rangeEnd} of {totalItems} companies
                  </p>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="px-3 py-1.5 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {pageSizeOptions?.map((size) => (
                        <option key={size} value={size} className="bg-gray-800">
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-500 px-2">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
